import type {StaticSearchParams} from 'src/typings';
import type {Restriction} from 'src/features/search/search-criteria/criteria.types';
import {
    FilterableDatatype,
    FilterOperatorEnum,
    type FilteredValue,
    type FilterOperator,
    type FilterValuesMap
} from './types';
import {exists} from 'shared/utils/custom-utils';
import {transferify} from 'shared/utils/data-service-utils';

interface CreateFilterQtProps {
    filteredValues?: FilterValuesMap;
    negatedFilteredValues?: FilterValuesMap;
    forcedValues?: FilterValuesMap;
}

const supportedFilterOperatorsForDatatype: Record<FilterableDatatype, FilterOperator[]> = {
    [FilterableDatatype.BOOLEAN]: [],
    [FilterableDatatype.DATE]: [FilterOperatorEnum.eqOrLess, FilterOperatorEnum.eqOrMore, FilterOperatorEnum.within],
    [FilterableDatatype.DATETIME]: [FilterOperatorEnum.eqOrLess, FilterOperatorEnum.eqOrMore, FilterOperatorEnum.within],
    [FilterableDatatype.DATETIME_RANGE]: [],
    [FilterableDatatype.NUMBER]: [FilterOperatorEnum.eqOrLess, FilterOperatorEnum.eqOrMore],
    [FilterableDatatype.TEXT]: [],
    [FilterableDatatype.CODEBOOK_VALUE]: [],
    [FilterableDatatype.RECORD]: []
}

export function createFilterQtString({
                                         filteredValues = {},
                                         forcedValues = {},
                                         negatedFilteredValues = {}
                                     }: CreateFilterQtProps): string | null {

    const joinedValues = {...filteredValues, ...forcedValues};

    if (Object.keys(joinedValues).length === 0 && Object.keys(negatedFilteredValues).length === 0) {
        return null;
    }

    const createSearchObject = (value: FilteredValue): Partial<Restriction> => {
        if (exists(value.filterOperator) && !supportedFilterOperatorsForDatatype[value.datatype].includes(value.filterOperator)) {
            throw new Error(`Filter operator ${value.filterOperator} is not supported for datatype ${value.datatype}`);
        }

        switch (value.datatype) {
            case FilterableDatatype.DATE:
            case FilterableDatatype.DATETIME: {
                if (exists(value.filterOperator)) {
                    if (value.filterOperator === FilterOperatorEnum.within) {
                        return {
                            within: {
                                value: value.val
                            }
                        };
                    }

                    return {
                        between: {
                            to: value.filterOperator === 'eqOrLess' ? value.val : null,
                            from: value.filterOperator === 'eqOrMore' ? value.val : null
                        }
                    };
                }

                return {
                    eq: {
                        value: value.val
                    }
                };
            }

            case FilterableDatatype.RECORD: {
                const searchedValue = typeof value.val === 'string' ? value.val : value.val.id;

                return {
                    eq: {
                        value: searchedValue
                    }
                };
            }

            case FilterableDatatype.TEXT: {
                return {
                    startsWithWords: {
                        value: value.val
                    }
                };
            }

            case FilterableDatatype.CODEBOOK_VALUE: {
                const searchedValue = typeof value.val === 'object' ? transferify(value.val, true) : value.val;

                return {
                    eq: {
                        value: searchedValue
                    }
                };
            }

            case FilterableDatatype.NUMBER: {
                if (exists(value.filterOperator)) {
                    return {
                        between: {
                            to: value.filterOperator === 'eqOrLess' ? value.val : null,
                            from: value.filterOperator === 'eqOrMore' ? value.val : null
                        }
                    };
                }

                return {
                    eq: {
                        value: value.val
                    }
                };
            }

            default: {
                const searchedValue = typeof value.val === 'object' ? transferify(value.val, true) : value.val;

                return {
                    eq: {
                        value: searchedValue
                    }
                };
            }
        }
    };

    return JSON.stringify({
        and: [
            ...Object.entries(joinedValues).map(([filterParam, value]) => {
                return {
                    field: filterParam,
                    ...createSearchObject(value)
                };
            }),
            ...Object.entries(negatedFilteredValues).map(([filterParam, value]) => {
                return {
                    not: {
                        field: filterParam,
                        ...createSearchObject(value)
                    }
                };
            })
        ]
    });
}

export function addFilterQtToSearchParams(params: Partial<StaticSearchParams>, {
    filteredValues = {},
    negatedFilteredValues = {},
    forcedValues = {}
}: CreateFilterQtProps): Partial<StaticSearchParams> {

    const qtString = createFilterQtString({
        filteredValues,
        negatedFilteredValues,
        forcedValues
    });

    if (!exists(qtString)) {
        return params;
    }

    return {
        ...params,
        qt: qtString
    };
}